import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import TemplatePreview from '../components/TemplatePreview';
import { templatesApi } from '../../../api/templates.api';
import apiClient from '../../../api/client';

const mapTemplate = (tpl = {}) => ({
  id: tpl.id || tpl.t_id || tpl._id || '',
  name: tpl.name || tpl.t_tname || '',
  category: tpl.category || tpl.t_type || '',
  language: tpl.language || tpl.t_language || '',
  status: tpl.status || tpl.t_status || '',
  headerType: tpl.headerType || tpl.t_header_type || tpl.header?.type || tpl.header?.format || 'none',
  header: tpl.header?.text || tpl.header || tpl.t_header || '',
  headerMediaUrl: tpl.headerMediaUrl || tpl.t_header_media_url || tpl.header?.mediaUrl || tpl.header?.url || tpl.header?.file || '',
  body: tpl.body?.text || tpl.body || tpl.t_btext || '',
  footer: tpl.footer?.text || tpl.footer || tpl.t_footer || '',
  buttons: (() => {
    if (Array.isArray(tpl.buttons)) return tpl.buttons;
    if (Array.isArray(tpl.buttons?.items)) return tpl.buttons.items;
    const previewButtons =
      tpl.preview?.components?.find((c) => c.type === 'BUTTONS')?.buttons ||
      tpl.preview?.components?.find((c) => c.type === 'BUTTONS')?.content?.buttons ||
      [];
    return Array.isArray(previewButtons) ? previewButtons : [];
  })(),
  createdAt: tpl.created_at || tpl.createdAt || '',
  variableSamples: tpl.variableSamples || tpl.variable_samples || tpl.t_variable_samples || { header: {}, body: {}, footer: {} },
});

const TemplateViewPage = ({ sess = { uid: '', role: 'user' } }) => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  };

  useEffect(() => {
    const fetchTemplate = async () => {
      setLoading(true);
      setError('');
      try {
        if (!id) {
          setError('No template id provided');
          setLoading(false);
          return;
        }
        const response = await apiClient.get(`/templates/${id}/preview`);
        const tpl = response?.data?.data || response?.data || {};
        setTemplate(mapTemplate(tpl));
      } catch (err) {
        setError('Failed to load template preview.');
        setTemplate(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id]);

  const previewData = template
    ? {
        headerType: template.headerType,
        header: template.header,
        headerMediaUrl: template.headerMediaUrl,
        body: template.body,
        footer: template.footer,
        buttons: template.buttons,
        variableSamples: template.variableSamples || {},
      }
    : {};

  return (
    <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button variant="outlined" startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/templates')} sx={secondaryButtonSx}>
              Back
            </Button>
            <Box>
              <Typography variant="h5" fontWeight={700}>Template Preview</Typography>
              <Typography variant="body2" color="text.secondary">
                Review layout before submission
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2} sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
                <Box>
                  <Skeleton variant="text" width={240} height={32} />
                  <Stack direction="row" spacing={1} mt={1}>
                    <Skeleton variant="rounded" width={80} height={24} />
                    <Skeleton variant="rounded" width={80} height={24} />
                    <Skeleton variant="rounded" width={80} height={24} />
                  </Stack>
                </Box>
                <Skeleton variant="text" width={200} height={20} />
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Skeleton variant="text" width={160} height={24} />
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5}>
                <Skeleton variant="rounded" height={140} />
                <Skeleton variant="rounded" height={140} />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      ) : !template ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Typography color="text.secondary">Template not found.</Typography>
        </Box>
      ) : (
        <Stack spacing={2} sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Card sx={cardSx}>
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {template.name || 'Untitled Template'}
                  </Typography>
                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                    {template.category && <Chip label={template.category} size="small" />}
                    {template.language && (
                      <Chip label={(template.language || '').toUpperCase()} size="small" variant="outlined" />
                    )}
                    {template.status && (
                      <Chip label={template.status} size="small" color={template.status === 'approved' ? 'success' : template.status === 'rejected' ? 'error' : 'warning'} />
                    )}
                  </Stack>
                </Box>
                {template.createdAt && (
                  <Typography variant="body2" color="text.secondary">
                    Created: {new Date(template.createdAt).toLocaleString()}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Message Preview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TemplatePreview template={previewData} />
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
};

export default TemplateViewPage;
